import {
  DevHubTypography,
  DevHubProgressBar,
  DevHubButton,
  DevHubDynamicIcon,
  devhubMakeStyles,
} from '@devxdiscover/devhub-ui';

const useStyles = devhubMakeStyles((theme: any) => ({
  resourceItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.devhubBackground.workbox,
  },
  resourceSvgIcon: {
    marginRight: theme.spacing(1.5),
    width: 20,
    height: 20,
    display: 'inline-block',
  },
  resourceLink: {
    flex: 1,
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    color: theme.palette.primary.main,
    textDecoration: 'none',
  },
  resourcePlaceholder: {
    flex: 1,
    height: 16,
    backgroundColor: theme.palette.devhubBackground.workbox,
    borderRadius: 4,
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  errorText: {
    color:
      theme.palette.type === 'dark'
        ? theme.palette.error.light
        : theme.palette.error.main,
    fontWeight: 600,
    flex: 1,
  },
  iconWrapper: {
    marginRight: theme.spacing(1.5),
    width: 20,
    height: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(255,255,255,0.15)' : 'transparent',
    borderRadius: 3,
    flexShrink: 0,
  },
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 1 },
  },
}));

export type ResourceProvisioningStatus =
  | 'pending'
  | 'in-progress'
  | 'completed'
  | 'failed'
  | 'skipped'
  | 'missing';

interface ResourceItemWithRetryProps {
  /** Path to the resource icon */
  iconSrc: string;
  /** Alt text for the icon */
  iconAlt: string;
  /** Resource name/identifier to display when completed */
  resourceName: string;
  /** Current provisioning status */
  status: ResourceProvisioningStatus;
  /** Progress percentage (0-100) for in-progress state */
  progress?: number;
  /** Error message for failed state */
  error?: string;
  /** Callback to retry provisioning */
  onRetry?: () => void;
  /** Whether retry is currently in progress */
  retrying?: boolean;
  /** Optional link for completed resources */
  href?: string;
}

/**
 * Resource item that displays different states: pending, in-progress, completed, failed, missing
 * Supports inline retry mechanism for failed or missing resources
 *
 * @param props - Component props
 */
export const ResourceItemWithRetry = ({
  iconSrc,
  iconAlt,
  resourceName,
  status,
  progress = 0,
  error,
  onRetry,
  retrying = false,
  href,
}: ResourceItemWithRetryProps) => {
  const classes = useStyles();

  // Completed or skipped - show resource with link/text
  if (status === 'completed' || status === 'skipped') {
    return (
      <div className={classes.resourceItem}>
        <img src={iconSrc} alt={iconAlt} className={classes.resourceSvgIcon} />
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.resourceLink}
          >
            {resourceName}
          </a>
        ) : (
          <span className={classes.resourceLink}>{resourceName}</span>
        )}
      </div>
    );
  }

  // Failed or missing - show error with retry button
  if (status === 'failed' || status === 'missing') {
    return (
      <div className={classes.resourceItem}>
        <span className={classes.iconWrapper}>
          <img
            src={iconSrc}
            alt={iconAlt}
            style={{ width: 16, height: 16, opacity: 0.7 }}
          />
        </span>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <DevHubTypography variant="body2" className={classes.errorText}>
            {`Provisioning failed${error ? `: ${error}` : ''}`}
          </DevHubTypography>
          {onRetry && (
            <DevHubButton
              variant="outlined"
              textColor="error"
              size="small"
              onClick={onRetry}
              disabled={retrying}
              startIcon={
                retrying ? undefined : <DevHubDynamicIcon iconName="Sync" />
              }
            >
              {retrying ? 'Retrying...' : 'Retry'}
            </DevHubButton>
          )}
        </div>
      </div>
    );
  }

  // In progress - show progress bar
  if (status === 'in-progress') {
    return (
      <div className={classes.resourceItem}>
        <img
          src={iconSrc}
          alt={iconAlt}
          className={classes.resourceSvgIcon}
          style={{ opacity: 0.5 }}
        />
        <div style={{ flex: 1 }}>
          <DevHubProgressBar value={progress} size="small" />
        </div>
      </div>
    );
  }

  // Pending - show placeholder
  return (
    <div className={classes.resourceItem}>
      <div
        className={classes.resourceSvgIcon}
        style={{ backgroundColor: 'transparent', borderRadius: '50%' }}
      />
      <div className={classes.resourcePlaceholder} />
    </div>
  );
};
